<template>
  <div class="whats-new-widget">
    <div v-if="hidden" class="hidden-state d-flex align-center justify-space-between px-1 py-2">
      <span class="text-caption grey--text">What's New is hidden</span>
      <v-btn x-small text color="green darken-1" @click="show">Show</v-btn>
    </div>
    <template v-else>
      <div class="d-flex align-center justify-end mb-1">
        <v-btn x-small text color="grey" @click="hide">
          <v-icon x-small left>mdi-eye-off-outline</v-icon>Hide
        </v-btn>
      </div>
      <div v-if="loading" class="text-center py-6">
        <v-progress-circular indeterminate size="24" color="green darken-1" />
      </div>
      <div v-else-if="fetchError" class="text-center py-4">
        <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
        <p class="text-caption grey--text mt-2">Unable to load updates</p>
      </div>
      <div v-else-if="updates.length === 0" class="text-center py-4">
        <v-icon color="grey lighten-1" size="32">mdi-newspaper-variant-outline</v-icon>
        <p class="text-caption grey--text mt-2">No recent updates</p>
      </div>
      <div v-else class="updates-list">
        <div
          v-for="update in updates"
          :key="update._id"
          class="update-item d-flex align-start"
        >
          <v-chip
            small
            :color="statusColor(update.status)"
            dark
            class="mr-3 mt-1 flex-shrink-0 status-chip"
            pill
          >
            {{ statusLabel(update.status) }}
          </v-chip>
          <div>
            <div class="text-body-2 font-weight-medium">
              {{ update.title }}
              <span v-if="update.version" class="text-caption grey--text ml-1">v{{ update.version }}</span>
            </div>
            <div class="text-caption grey--text">{{ update.body }}</div>
            <a
              v-if="update.link"
              :href="update.link"
              target="_blank"
              rel="noopener noreferrer"
              class="text-caption font-weight-medium guide-link d-inline-flex align-center mt-1"
            >
              View guide
              <v-icon
                x-small
                :color="$vuetify.theme.dark ? 'green lighten-2' : 'green darken-1'"
                class="ml-1"
              >mdi-open-in-new</v-icon>
            </a>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { clients } from '../../../util/clients';

const { backendRest } = clients.direct;
const STORAGE_KEY = 'whatsNewWidget_hidden';

export default {
  name: 'WhatsNewWidget',
  data: () => ({
    updates: [],
    loading: true,
    fetchError: false,
    hidden: localStorage.getItem(STORAGE_KEY) === 'true',
  }),
  computed: {
    ...mapGetters('auth/token', ['authUser']),
  },
  async created() {
    if (!this.hidden) await this.fetchUpdates();
  },
  methods: {
    async fetchUpdates() {
      if (this.authUser === 'anonymous') { this.loading = false; return; }
      this.loading = true;
      this.fetchError = false;
      try {
        const resp = await backendRest.get('/dashboard/release-notes');
        /* The endpoint filters to published and sorts newest-first before it answers, so this
           keeps the ten newest published notes. Do not re-sort here: the backend's tie-break for
           two notes published in the same second is _id descending, which this list cannot
           reproduce in a way that would stay in step with it. */
        this.updates = (resp.data || []).slice(0, 10);
      } catch (e) {
        console.error('WhatsNewWidget: failed to load', e);
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },
    hide() {
      this.hidden = true;
      localStorage.setItem(STORAGE_KEY, 'true');
    },
    async show() {
      this.hidden = false;
      localStorage.setItem(STORAGE_KEY, 'false');
      await this.fetchUpdates();
    },
    statusColor(status) {
      const colors = {
        deployed: 'green darken-1',
        'in-uat': 'blue darken-1',
        'coming-soon': 'orange darken-1',
      };
      return colors[status] || 'grey';
    },
    statusLabel(status) {
      const labels = {
        deployed: 'Live',
        'in-uat': 'Testing',
        'coming-soon': 'Coming Soon',
      };
      return labels[status] || (status || 'Update');
    },
    async refresh() {
      if (!this.hidden) await this.fetchUpdates();
    },
  },
};
</script>

<style scoped>
.update-item {
  padding: 10px 12px;
  border-radius: 10px;
  transition: background 0.15s ease;
  margin-bottom: 6px;
}

.update-item:hover {
  background: var(--widget-hover-bg, rgba(0, 0, 0, 0.03));
}

.update-item:last-child {
  margin-bottom: 0;
}

.updates-list {
  max-height: 320px;
  overflow-y: auto;
}

.updates-list::-webkit-scrollbar {
  width: 4px;
}

.updates-list::-webkit-scrollbar-thumb {
  background: var(--widget-scrollbar-thumb, #c8e6c9);
  border-radius: 4px;
}

.guide-link {
  text-decoration: none;
  color: var(--widget-ok-text, #2e7d32);
}

.guide-link:hover {
  text-decoration: underline;
}

.status-chip {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  min-width: 72px;
  justify-content: center;
}

.hidden-state {
  border: 1px dashed var(--widget-dashed-border, #e0e0e0);
  border-radius: 8px;
}
</style>
